// admin/src/pages/ScimIntegrations/index.js

import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Stack,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RefreshIcon from '@mui/icons-material/Refresh';

import ReusableHeader from '../../components/Header/ReusableHeader';
import ReusableTable from '../../components/Table/ReusableTable';
import { ColorModeContext } from '../../index';

import {
  SCIM_PERMISSIONS,
  hasScimPermission,
} from './scimPermissions';

import {
  listConnections,
  createConnection,
  getConnectionStatus,
  listActivities,
  listConflicts,
  resolveConflict,
  extractScimErrorMessage,
} from './scimApi';

import TokenModal from './TokenModal';
import ConnectionDetailDrawer from './ConnectionDetailDrawer';
import OutboundTargetDialog from './OutboundTargetDialog';
import OutboundTargetDrawer from './OutboundTargetDrawer';
import {
  listOutboundTargets,
  extractOutboundErrorMessage,
} from './scimOutboundApi';
import { getStatusChipSx } from './scimStatusStyle';

/* =========================================================
   mEDGE DESIGN TOKENS
========================================================= */
const LIGHT_VARS = {
  '--vault-navy': '#0B2545',
  '--edge-blue': '#1F6FEB',
  '--canvas': '#FFFFFF',
  '--mist': '#F7F9FC',
  '--hairline': '#E4E9F0',
  '--blue-tint': '#EAF2FE',
  '--slate': '#6B7A90',
  '--secure': '#00A88F',
  '--high': '#E2622B',
  '--elevated': '#F0A202',
  '--critical': '#D6323A',
  '--border-radius': '6px',
};

const DARK_VARS = {
  '--vault-navy': '#F4F7FB',
  '--edge-blue': '#4C9AFF',
  '--canvas': '#0F1729',
  '--mist': '#252b3b',
  '--hairline': 'rgba(255,255,255,0.14)',
  '--blue-tint': 'rgba(76,154,255,0.16)',
  '--slate': '#93A4BD',
  '--secure': '#2FD3B8',
  '--high': '#F07C46',
  '--elevated': '#FFC24B',
  '--critical': '#FF6B72',
  '--border-radius': '6px',
};

export default function ScimIntegrationsPage() {
  const { mode } = useContext(ColorModeContext || { mode: 'light' });
  const V = mode === 'dark' ? DARK_VARS : LIGHT_VARS;

  // Stored user principal
  const currentUser = useMemo(() => {
    try {
      const raw = typeof window !== 'undefined' ? (localStorage.getItem('user') || sessionStorage.getItem('user')) : null;
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  // Strict permission gating: decided ONLY by permission strings, without role-name checks
  const canRead = hasScimPermission(currentUser, SCIM_PERMISSIONS.READ);
  const canCreate = hasScimPermission(currentUser, SCIM_PERMISSIONS.CREATE);
  const canUpdate = hasScimPermission(currentUser, SCIM_PERMISSIONS.UPDATE);
  const canDelete = hasScimPermission(currentUser, SCIM_PERMISSIONS.DELETE);

  // Tab State: 0 = Overview, 1 = Connections, 2 = Outbound, 3 = Activity, 4 = Conflicts
  const [activeTab, setActiveTab] = useState(0);

  // Outbound Targets State
  const [outboundTargets, setOutboundTargets] = useState([]);
  const [outboundTotal, setOutboundTotal] = useState(0);
  const [outboundPage, setOutboundPage] = useState(0);
  const [outboundRowsPerPage, setOutboundRowsPerPage] = useState(10);
  const [outboundLoading, setOutboundLoading] = useState(false);
  const [outboundError, setOutboundError] = useState(null);
  const [createTargetDialogOpen, setCreateTargetDialogOpen] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState(null);
  const [targetDrawerOpen, setTargetDrawerOpen] = useState(false);

  // Connection data state
  const [connections, setConnections] = useState([]);
  const [connectionsTotal, setConnectionsTotal] = useState(0);
  const [connPage, setConnPage] = useState(0);
  const [connRowsPerPage, setConnRowsPerPage] = useState(10);
  const [connLoading, setConnLoading] = useState(false);
  const [connError, setConnError] = useState(null);

  // Overview aggregated stats state
  const [overviewMetrics, setOverviewMetrics] = useState({
    totalConnections: 0,
    activeConnections: 0,
    inactiveConnections: 0,
    success24h: 0,
    failure24h: 0,
    pendingConflicts: 0,
  });
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState(null);

  // Create Connection Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createProvider, setCreateProvider] = useState('generic');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState(null);

  // One-time Plaintext Token Modal state (cleared immediately upon close)
  const [tokenModalData, setTokenModalData] = useState(null);

  // Connection Detail Drawer state
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Activity Tab State
  const [activityConnId, setActivityConnId] = useState('');
  const [activityOutcome, setActivityOutcome] = useState('all');
  const [activityMethod, setActivityMethod] = useState('all');
  const [activities, setActivities] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRowsPerPage, setActivityRowsPerPage] = useState(20);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  // Conflicts Tab State
  const [conflictConnId, setConflictConnId] = useState('');
  const [conflictStatus, setConflictStatus] = useState('all');
  const [conflicts, setConflicts] = useState([]);
  const [conflictTotal, setConflictTotal] = useState(0);
  const [conflictPage, setConflictPage] = useState(0);
  const [conflictRowsPerPage, setConflictRowsPerPage] = useState(20);
  const [conflictLoading, setConflictLoading] = useState(false);
  const [conflictError, setConflictError] = useState(null);

  // Conflict Resolution Dialog state
  const [resolveTarget, setResolveTarget] = useState(null);
  const [resolutionStatus, setResolutionStatus] = useState('resolved');
  const [resolutionAction, setResolutionAction] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolveBusy, setResolveBusy] = useState(false);
  const [resolveError, setResolveError] = useState(null);

  /* =====================================================
     LOAD CONNECTIONS LIST
  ===================================================== */
  const loadConnectionsList = async () => {
    if (!canRead) return;
    setConnLoading(true);
    setConnError(null);
    try {
      const res = await listConnections({
        page: connPage + 1,
        limit: connRowsPerPage,
      });
      if (res?.data) {
        setConnections(res.data);
        setConnectionsTotal(res.pagination?.total || res.data.length);

        // Pre-select first connection for Activity & Conflicts tabs if not selected
        if (!activityConnId && res.data.length > 0) {
          setActivityConnId(res.data[0]._id);
        }
        if (!conflictConnId && res.data.length > 0) {
          setConflictConnId(res.data[0]._id);
        }
      }
    } catch (err) {
      setConnError(extractScimErrorMessage(err));
    } finally {
      setConnLoading(false);
    }
  };

  /* =====================================================
     LOAD OUTBOUND TARGETS LIST
  ===================================================== */
  const loadOutboundTargetsList = async () => {
    if (!canRead) return;
    setOutboundLoading(true);
    setOutboundError(null);
    try {
      const res = await listOutboundTargets({
        page: outboundPage + 1,
        limit: outboundRowsPerPage,
      });
      if (res?.data) {
        setOutboundTargets(res.data);
        setOutboundTotal(res.pagination?.total || res.data.length);
      }
    } catch (err) {
      setOutboundError(extractOutboundErrorMessage(err));
    } finally {
      setOutboundLoading(false);
    }
  };

  /* =====================================================
     LOAD OVERVIEW AGGREGATION
  ===================================================== */
  const loadOverviewStats = async () => {
    if (!canRead) return;
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      // Step 1: Fetch list of connections up to cap (20)
      const listRes = await listConnections({ page: 1, limit: 20 });
      const conns = listRes?.data || [];
      const total = listRes?.pagination?.total || conns.length;

      let activeCount = 0;
      let inactiveCount = 0;
      let successes = 0;
      let failures = 0;
      let conflictsPending = 0;

      conns.forEach((c) => {
        if (c.status === 'active') activeCount++;
        else inactiveCount++;
      });

      // Populate connections list if currently empty
      if (conns.length > 0) {
        setConnections(conns);
        setConnectionsTotal(total);
        setActivityConnId((prev) => prev || conns[0]._id);
        setConflictConnId((prev) => prev || conns[0]._id);
      }

      // Step 2: Query operational status for connections in parallel
      const statusPromises = conns.map((c) =>
        getConnectionStatus(c._id).catch(() => null)
      );
      const statusResults = await Promise.all(statusPromises);

      statusResults.forEach((statusRes) => {
        if (statusRes?.data) {
          const d = statusRes.data;
          successes += d.metrics?.successCount24h || 0;
          failures += d.metrics?.failureCount24h || 0;
          conflictsPending += d.pendingConflictsCount || 0;
        }
      });

      setOverviewMetrics({
        totalConnections: total,
        activeConnections: activeCount,
        inactiveConnections: inactiveCount,
        success24h: successes,
        failure24h: failures,
        pendingConflicts: conflictsPending,
      });
    } catch (err) {
      setOverviewError(extractScimErrorMessage(err));
    } finally {
      setOverviewLoading(false);
    }
  };

  /* =====================================================
     LOAD ACTIVITIES
  ===================================================== */
  const loadActivitiesList = async (targetConnId = activityConnId) => {
    const connId = targetConnId || activityConnId || (Array.isArray(connections) && connections[0]?._id);
    if (!canRead || !connId) return;
    setActivityLoading(true);
    setActivityError(null);
    try {
      const res = await listActivities(connId, {
        page: activityPage + 1,
        limit: activityRowsPerPage,
        outcome: activityOutcome,
        method: activityMethod,
      });
      const acts = Array.isArray(res?.data) ? res.data : [];
      setActivities(acts);
      setActivityTotal(res?.pagination?.total || acts.length);
    } catch (err) {
      setActivityError(extractScimErrorMessage(err));
    } finally {
      setActivityLoading(false);
    }
  };

  /* =====================================================
     LOAD CONFLICTS
  ===================================================== */
  const loadConflictsList = async (targetConnId = conflictConnId) => {
    const connId = targetConnId || conflictConnId || (Array.isArray(connections) && connections[0]?._id);
    if (!canRead || !connId) return;
    setConflictLoading(true);
    setConflictError(null);
    try {
      const res = await listConflicts(connId, {
        page: conflictPage + 1,
        limit: conflictRowsPerPage,
        resolutionStatus: conflictStatus,
      });
      const confs = Array.isArray(res?.data) ? res.data : [];
      setConflicts(confs);
      setConflictTotal(res?.pagination?.total || confs.length);
    } catch (err) {
      setConflictError(extractScimErrorMessage(err));
    } finally {
      setConflictLoading(false);
    }
  };

  // Effect 1: Tab 0 (Overview) & Tab 1 (Connections)
  useEffect(() => {
    if (!canRead) return;
    if (activeTab === 0) {
      loadOverviewStats();
    } else if (activeTab === 1) {
      loadConnectionsList();
    }
  }, [activeTab, canRead, connPage, connRowsPerPage]);

  // Effect 2: Tab 2 (Outbound)
  useEffect(() => {
    if (!canRead || activeTab !== 2) return;
    loadOutboundTargetsList();
  }, [activeTab, canRead, outboundPage, outboundRowsPerPage]);

  // Effect 3: Tab 3 (Activities)
  useEffect(() => {
    if (!canRead || activeTab !== 3) return;
    const connId = activityConnId || (Array.isArray(connections) && connections[0]?._id);
    if (connId) {
      loadActivitiesList(connId);
    } else if (connections.length === 0) {
      loadConnectionsList();
    }
  }, [activeTab, canRead, activityConnId, activityOutcome, activityMethod, activityPage, activityRowsPerPage]);

  // Effect 4: Tab 4 (Conflicts)
  useEffect(() => {
    if (!canRead || activeTab !== 4) return;
    const connId = conflictConnId || (Array.isArray(connections) && connections[0]?._id);
    if (connId) {
      loadConflictsList(connId);
    } else if (connections.length === 0) {
      loadConnectionsList();
    }
  }, [activeTab, canRead, conflictConnId, conflictStatus, conflictPage, conflictRowsPerPage]);

  /* =====================================================
     CREATE CONNECTION HANDLER
  ===================================================== */
  const handleCreateConnection = async () => {
    if (!createName.trim()) {
      setCreateError('Connection name is required.');
      return;
    }
    setCreateBusy(true);
    setCreateError(null);
    try {
      const res = await createConnection({
        name: createName.trim(),
        providerType: createProvider,
      });
      setCreateDialogOpen(false);
      setCreateName('');
      setCreateProvider('generic');

      // Refresh connections and overview
      await loadConnectionsList();

      // Open one-time token modal
      if (res?.data?.credentials?.plaintextToken) {
        setTokenModalData({
          plaintextToken: res.data.credentials.plaintextToken,
          tokenPrefix: res.data.credentials.tokenPrefix,
          tokenId: res.data.credentials.tokenId,
        });
      }
    } catch (err) {
      setCreateError(extractScimErrorMessage(err));
    } finally {
      setCreateBusy(false);
    }
  };

  /* =====================================================
     RESOLVE CONFLICT HANDLER
  ===================================================== */
  const handleResolveConflict = async () => {
    if (!resolveTarget || !canUpdate) return;
    setResolveBusy(true);
    setResolveError(null);
    try {
      await resolveConflict(conflictConnId, resolveTarget._id, {
        resolutionStatus,
        resolutionAction: resolutionAction.trim() || undefined,
        resolutionNote: resolutionNote.trim() || undefined,
      });
      setResolveTarget(null);
      setResolutionAction('');
      setResolutionNote('');
      await loadConflictsList();
    } catch (err) {
      setResolveError(extractScimErrorMessage(err));
    } finally {
      setResolveBusy(false);
    }
  };

  /* =====================================================
     ACCESS DENIED STATE
     Rendered if user lacks Read permission, 0 requests sent
  ===================================================== */
  if (!canRead) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <ReusableHeader title="SCIM Integrations" />
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 340,
            mt: 3,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <SecurityIcon color="error" sx={{ fontSize: 32 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
            You do not have permission to view SCIM Integrations. Please contact your organization administrator to
            request access.
          </Typography>
        </Paper>
      </Container>
    );
  }

  /* =====================================================
     CONNECTIONS TABLE COLUMNS
  ===================================================== */
  const connectionColumns = [
    {
      id: 'name',
      label: 'Connection Name',
      render: (row) => (
        <div>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {row.connectionPublicId?.slice(0, 8)}...
          </Typography>
        </div>
      ),
    },
    {
      id: 'providerType',
      label: 'Provider',
      render: (row) => (
        <Chip
          size="small"
          label={row.providerType?.toUpperCase()}
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => (
        <Chip
          size="small"
          label={row.status}
          sx={getStatusChipSx(row.status, mode)}
        />
      ),
    },
    {
      id: 'activeTokens',
      label: 'Active Tokens',
      render: (row) => (
        <Typography variant="body2">
          {Array.isArray(row.activeCredentials) ? row.activeCredentials.length : 0}
        </Typography>
      ),
    },
    {
      id: 'lastSyncAt',
      label: 'Last Sync',
      render: (row) => (
        <Typography variant="body2">
          {row.lastSyncAt ? new Date(row.lastSyncAt).toLocaleString() : 'Never'}
        </Typography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      render: (row) => (
        <Typography variant="body2">
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
          onClick={() => {
            setSelectedConnectionId(row._id);
            setDrawerOpen(true);
          }}
          id={`manage-connection-${row._id}`}
        >
          Manage
        </Button>
      ),
    },
  ];

  /* =====================================================
     OUTBOUND TARGETS TABLE COLUMNS
  ===================================================== */
  const outboundColumns = [
    {
      id: 'name',
      label: 'Target Name',
      render: (row) => (
        <div>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID: {row._id ? `${row._id.slice(0, 8)}...` : '—'}
          </Typography>
        </div>
      ),
    },
    {
      id: 'baseUrl',
      label: 'Base URL',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {row.baseUrl}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => (
        <Chip
          size="small"
          label={row.status}
          sx={getStatusChipSx(row.status, mode)}
        />
      ),
    },
    {
      id: 'mode',
      label: 'Mode',
      render: (row) =>
        row.dryRun ? (
          <Chip size="small" label="DRY RUN" color="warning" sx={{ fontWeight: 700 }} />
        ) : (
          <Chip size="small" label="LIVE" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
        ),
    },
    {
      id: 'scope',
      label: 'Scope',
      render: (row) => (
        <Typography variant="body2">
          {row.scope?.type === 'all'
            ? 'All users'
            : row.scope?.type === 'group'
              ? `Group (${row.scope?.groupIds?.length || 0})`
              : `Users (${row.scope?.userIds?.length || 0})`}
        </Typography>
      ),
    },
    {
      id: 'lastSyncAt',
      label: 'Last Sync',
      render: (row) => (
        <Typography variant="body2">
          {row.lastSyncAt ? new Date(row.lastSyncAt).toLocaleString() : 'Never'}
        </Typography>
      ),
    },
    {
      id: 'lastErrorCategory',
      label: 'Last Error',
      render: (row) => (
        <Typography variant="caption" color="text.secondary">
          {row.lastErrorCategory || '—'}
        </Typography>
      ),
    },
    {
      id: 'counts',
      label: 'Counts',
      render: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Chip size="small" label={`Synced: ${row.counts?.synced || 0}`} color="success" variant="outlined" />
          <Chip size="small" label={`Failed: ${row.counts?.failed || 0}`} color={row.counts?.failed ? 'error' : 'default'} variant="outlined" />
        </Stack>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
          onClick={() => {
            setSelectedTargetId(row._id);
            setTargetDrawerOpen(true);
          }}
          id={`manage-target-${row._id}`}
        >
          Manage
        </Button>
      ),
    },
  ];

  /* =====================================================
     ACTIVITIES TABLE COLUMNS
  ===================================================== */
  const activityColumns = [
    {
      id: 'timestamp',
      label: 'Timestamp',
      render: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
        </Typography>
      ),
    },
    {
      id: 'method',
      label: 'Method',
      render: (row) => (
        <Chip
          size="small"
          label={row.method}
          variant="outlined"
          sx={{
            fontWeight: 700,
            fontSize: '0.75rem',
            fontFamily: 'monospace',
          }}
        />
      ),
    },
    {
      id: 'resource',
      label: 'Resource',
      render: (row) => (
        <Typography variant="body2">
          {row.resourceType}
          {row.scimResourceId ? ` (${row.scimResourceId.slice(0, 8)}...)` : ''}
        </Typography>
      ),
    },
    {
      id: 'outcome',
      label: 'Outcome',
      render: (row) => (
        <Chip
          size="small"
          label={row.outcome}
          color={row.outcome === 'success' ? 'success' : 'error'}
          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
        />
      ),
    },
    {
      id: 'httpStatus',
      label: 'Status Code',
      render: (row) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {row.httpStatus}
        </Typography>
      ),
    },
    {
      id: 'duration',
      label: 'Duration',
      render: (row) => (
        <Typography variant="body2">
          {row.durationMs !== undefined ? `${row.durationMs}ms` : '—'}
        </Typography>
      ),
    },
    {
      id: 'requestId',
      label: 'Request ID',
      render: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {row.correlationId || '—'}
        </Typography>
      ),
    },
    {
      id: 'attributes',
      label: 'Attribute Names',
      render: (row) => {
        // Show attribute NAMES only if returned; NEVER render bodies or values!
        if (!Array.isArray(row.changedAttributes) || row.changedAttributes.length === 0) {
          return <Typography variant="caption" color="text.secondary">—</Typography>;
        }
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {row.changedAttributes.map((attr, idx) => (
              <Chip
                key={idx}
                size="small"
                label={attr}
                sx={{ fontSize: '0.72rem', height: 20 }}
              />
            ))}
          </Stack>
        );
      },
    },
  ];

  /* =====================================================
     CONFLICTS TABLE COLUMNS
  ===================================================== */
  const conflictColumns = [
    {
      id: 'scimUserName',
      label: 'SCIM Username',
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.scimUserName}
        </Typography>
      ),
    },
    {
      id: 'externalId',
      label: 'External ID',
      render: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {row.scimExternalId || '—'}
        </Typography>
      ),
    },
    {
      id: 'conflictingType',
      label: 'Conflict Type',
      render: (row) => (
        <Chip
          size="small"
          label={row.conflictingUserType}
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      ),
    },
    {
      id: 'conflictReason',
      label: 'Reason',
      render: (row) => (
        <Typography variant="body2">
          {row.conflictReason}
        </Typography>
      ),
    },
    {
      id: 'resolutionStatus',
      label: 'Status',
      render: (row) => (
        <Chip
          size="small"
          label={row.resolutionStatus}
          sx={getStatusChipSx(row.resolutionStatus, mode)}
        />
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      render: (row) => (
        <Typography variant="body2">
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        row.resolutionStatus === 'pending' && canUpdate ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setResolveTarget(row);
              setResolutionStatus('resolved');
              setResolutionAction('');
              setResolutionNote('');
              setResolveError(null);
            }}
            id={`resolve-conflict-${row._id}`}
          >
            Resolve
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {row.resolutionStatus === 'resolved' ? 'Resolved' : row.resolutionStatus === 'ignored' ? 'Ignored' : '—'}
          </Typography>
        )
      ),
    },
  ];

  return (
    <div style={V}>
      <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
        <ReusableHeader
          title="SCIM Integrations"
          breadcrumbs={[{ label: 'SCIM Integrations' }]}
          actionButton={
            activeTab === 1 && canCreate ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCreateName('');
                  setCreateProvider('generic');
                  setCreateError(null);
                  setCreateDialogOpen(true);
                }}
                id="open-create-connection-dialog-btn"
              >
                Create Connection
              </Button>
            ) : activeTab === 2 && canCreate ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateTargetDialogOpen(true)}
                id="open-create-target-dialog-btn"
              >
                Add Target
              </Button>
            ) : null
          }
        />

        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            aria-label="SCIM Integrations tabs"
          >
            <Tab label="Overview" id="scim-tab-overview" />
            <Tab label="Connections" id="scim-tab-connections" />
            <Tab label="Outbound" id="scim-tab-outbound" />
            <Tab label="Activity" id="scim-tab-activity" />
            <Tab label="Conflicts" id="scim-tab-conflicts" />
          </Tabs>
        </Box>

        {/* TAB 0: OVERVIEW */}
        {activeTab === 0 && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Metrics aggregated from real connection telemetry (capped at first 20 connections).
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadOverviewStats}
                disabled={overviewLoading}
              >
                Refresh
              </Button>
            </Stack>

            {overviewLoading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress size={36} />
              </Box>
            ) : overviewError ? (
              <Alert severity="error" sx={{ mb: 3 }}>
                {overviewError}
              </Alert>
            ) : (
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        Total Connections
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {overviewMetrics.totalConnections}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Provisioning endpoints
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        Active vs Suspended
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {overviewMetrics.activeConnections} / {overviewMetrics.inactiveConnections}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active / Disabled or Suspended
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        Operations (24h)
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {overviewMetrics.success24h} / {overviewMetrics.failure24h}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Successes / Failures
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">
                        Pending Conflicts
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          mt: 0.5,
                          color: overviewMetrics.pendingConflicts > 0 ? 'warning.main' : 'inherit',
                        }}
                      >
                        {overviewMetrics.pendingConflicts}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Unresolved identity collisions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        {/* TAB 1: CONNECTIONS */}
        {activeTab === 1 && (
          <Box>
            {connError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {connError}
              </Alert>
            )}

            <ReusableTable
              columns={connectionColumns}
              data={Array.isArray(connections) ? connections : []}
              loading={connLoading}
              page={connPage}
              rowsPerPage={connRowsPerPage}
              total={connectionsTotal}
              onPageChange={(e, newPage) => setConnPage(newPage)}
              onRowsPerPageChange={(e) => setConnRowsPerPage(parseInt(e.target.value, 10))}
              emptyMessage="No SCIM connections found. Click 'Create Connection' to start."
            />
          </Box>
        )}

        {/* TAB 2: OUTBOUND */}
        {activeTab === 2 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }} id="outbound-banner-alert">
              Outbound provisioning pushes IAM users to external apps. Real third-party compatibility is UNVERIFIED until tested per app.
            </Alert>

            {outboundError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {outboundError}
              </Alert>
            )}

            <ReusableTable
              columns={outboundColumns}
              data={Array.isArray(outboundTargets) ? outboundTargets : []}
              loading={outboundLoading}
              page={outboundPage}
              rowsPerPage={outboundRowsPerPage}
              total={outboundTotal}
              onPageChange={(e, newPage) => setOutboundPage(newPage)}
              onRowsPerPageChange={(e) => setOutboundRowsPerPage(parseInt(e.target.value, 10))}
              emptyMessage="No outbound targets configured. Click 'Add Target' to get started."
            />
          </Box>
        )}

        {/* TAB 3: ACTIVITY */}
        {activeTab === 3 && (
          <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="activity-conn-label">Select Connection</InputLabel>
                    <Select
                      labelId="activity-conn-label"
                      value={activityConnId}
                      label="Select Connection"
                      onChange={(e) => setActivityConnId(e.target.value)}
                      id="activity-conn-select"
                    >
                      {(Array.isArray(connections) ? connections : []).map((c) => (
                        <MenuItem key={c._id} value={c._id}>
                          {c.name} ({c.providerType})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="activity-outcome-label">Outcome</InputLabel>
                    <Select
                      labelId="activity-outcome-label"
                      value={activityOutcome}
                      label="Outcome"
                      onChange={(e) => setActivityOutcome(e.target.value)}
                      id="activity-outcome-select"
                    >
                      <MenuItem value="all">All Outcomes</MenuItem>
                      <MenuItem value="success">Success</MenuItem>
                      <MenuItem value="failure">Failure</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="activity-method-label">HTTP Method</InputLabel>
                    <Select
                      labelId="activity-method-label"
                      value={activityMethod}
                      label="HTTP Method"
                      onChange={(e) => setActivityMethod(e.target.value)}
                      id="activity-method-select"
                    >
                      <MenuItem value="all">All Methods</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                      <MenuItem value="PATCH">PATCH</MenuItem>
                      <MenuItem value="DELETE">DELETE</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {activityError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {activityError}
              </Alert>
            )}

            <ReusableTable
              columns={activityColumns}
              data={Array.isArray(activities) ? activities : []}
              loading={activityLoading}
              page={activityPage}
              rowsPerPage={activityRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              total={activityTotal}
              onPageChange={(e, newPage) => setActivityPage(newPage)}
              onRowsPerPageChange={(e) => setActivityRowsPerPage(parseInt(e.target.value, 10))}
              emptyMessage={activityConnId ? 'No activity logs found for this connection.' : 'Please select a connection above.'}
            />
          </Box>
        )}

        {/* TAB 4: CONFLICTS */}
        {activeTab === 4 && (
          <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="conflict-conn-label">Select Connection</InputLabel>
                    <Select
                      labelId="conflict-conn-label"
                      value={conflictConnId}
                      label="Select Connection"
                      onChange={(e) => setConflictConnId(e.target.value)}
                      id="conflict-conn-select"
                    >
                      {(Array.isArray(connections) ? connections : []).map((c) => (
                        <MenuItem key={c._id} value={c._id}>
                          {c.name} ({c.providerType})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="conflict-status-label">Resolution Status</InputLabel>
                    <Select
                      labelId="conflict-status-label"
                      value={conflictStatus}
                      label="Resolution Status"
                      onChange={(e) => setConflictStatus(e.target.value)}
                      id="conflict-status-select"
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="ignored">Ignored</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {conflictError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {conflictError}
              </Alert>
            )}

            <ReusableTable
              columns={conflictColumns}
              data={Array.isArray(conflicts) ? conflicts : []}
              loading={conflictLoading}
              page={conflictPage}
              rowsPerPage={conflictRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              total={conflictTotal}
              onPageChange={(e, newPage) => setConflictPage(newPage)}
              onRowsPerPageChange={(e) => setConflictRowsPerPage(parseInt(e.target.value, 10))}
              emptyMessage={conflictConnId ? 'No conflicts recorded for this connection.' : 'Please select a connection above.'}
            />
          </Box>
        )}

        {/* Create Connection Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => !createBusy && setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          aria-labelledby="create-connection-dialog-title"
        >
          <DialogTitle id="create-connection-dialog-title" sx={{ fontWeight: 700 }}>
            Create Inbound SCIM 2.0 Connection
          </DialogTitle>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Notice on Provider Presets
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Provider presets (Entra, Okta, etc.) are <strong>UNVERIFIED</strong> on real production tenants.
                Verify attribute mappings in the configuration before enabling full sync.
              </Typography>
            </Alert>

            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createError}
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              label="Connection Name"
              placeholder="e.g. Corporate Entra ID Sync"
              fullWidth
              required
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              id="create-connection-name-input"
            />

            <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
              <InputLabel id="create-provider-label">Provider Type</InputLabel>
              <Select
                labelId="create-provider-label"
                value={createProvider}
                label="Provider Type"
                onChange={(e) => setCreateProvider(e.target.value)}
                id="create-provider-select"
              >
                <MenuItem value="generic">Generic SCIM 2.0</MenuItem>
                <MenuItem value="entra">Microsoft Entra ID</MenuItem>
                <MenuItem value="okta">Okta</MenuItem>
                <MenuItem value="ping">PingFederate</MenuItem>
                <MenuItem value="onelogin">OneLogin</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setCreateDialogOpen(false)} color="inherit" disabled={createBusy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateConnection}
              disabled={!createName.trim() || createBusy}
              id="submit-create-connection-btn"
              data-testid="submit-create-connection-btn"
            >
              {createBusy ? <CircularProgress size={18} /> : 'Create Connection'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Conflict Resolution Dialog */}
        <Dialog
          open={Boolean(resolveTarget)}
          onClose={() => !resolveBusy && setResolveTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Resolve Conflict</DialogTitle>
          <DialogContent dividers>
            {resolveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {resolveError}
              </Alert>
            )}
            <DialogContentText sx={{ mb: 2 }}>
              Resolve collision for user <strong>{resolveTarget?.scimUserName}</strong> ({resolveTarget?.conflictReason}):
            </DialogContentText>

            <FormControl fullWidth size="small" margin="dense">
              <InputLabel id="res-status-label">Resolution Status</InputLabel>
              <Select
                labelId="res-status-label"
                value={resolutionStatus}
                label="Resolution Status"
                onChange={(e) => setResolutionStatus(e.target.value)}
                id="res-status-select"
              >
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="ignored">Ignored</MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="dense"
              fullWidth
              size="small"
              label="Action Taken"
              placeholder="e.g. Merged with existing local account"
              value={resolutionAction}
              onChange={(e) => setResolutionAction(e.target.value)}
              id="res-action-input"
            />

            <TextField
              margin="dense"
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Resolution Note"
              placeholder="Optional notes for audit logs"
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              id="res-note-input"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setResolveTarget(null)} color="inherit" disabled={resolveBusy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleResolveConflict}
              disabled={resolveBusy}
              id="submit-resolve-conflict-btn"
            >
              {resolveBusy ? <CircularProgress size={18} /> : 'Save Resolution'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Connection Detail Drawer */}
        <ConnectionDetailDrawer
          open={drawerOpen}
          connectionId={selectedConnectionId}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedConnectionId(null);
          }}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onTokenIssued={(data) => {
            setTokenModalData(data);
          }}
          onConnectionDeleted={() => {
            loadConnectionsList();
            loadOverviewStats();
          }}
          onConnectionUpdated={() => {
            loadConnectionsList();
            loadOverviewStats();
          }}
        />

        {/* One-time Plaintext Token Modal */}
        <TokenModal
          open={Boolean(tokenModalData)}
          tokenData={tokenModalData}
          onClose={() => {
            // Strictly clear token from component state
            setTokenModalData(null);
          }}
        />

        {/* Outbound Target Dialog */}
        <OutboundTargetDialog
          open={createTargetDialogOpen}
          onClose={() => setCreateTargetDialogOpen(false)}
          onSuccess={() => loadOutboundTargetsList()}
        />

        {/* Outbound Target Drawer */}
        <OutboundTargetDrawer
          open={targetDrawerOpen}
          targetId={selectedTargetId}
          onClose={() => {
            setTargetDrawerOpen(false);
            setSelectedTargetId(null);
          }}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onTargetDeleted={() => {
            loadOutboundTargetsList();
            setSelectedTargetId(null);
          }}
          onTargetUpdated={() => {
            loadOutboundTargetsList();
          }}
        />
      </Container>
    </div>
  );
}
